import React, { useState, useEffect, useCallback } from "react";

const t = () => new Proxy({}, { get: (_, p) => (window as any).__ENGPORTAL_THEME?.[p] }) as any;

// ─── Report definitions ─────────────────────────────────────────────────────
const REPORTS = [
  {
    id: 'process-yields', name: 'Process Yields', category: 'Production',
    description: 'Yield percentages by part, operation, and date range',
    params: [
      { key: 'StartDate', label: 'Start Date', type: 'date' },
      { key: 'EndDate', label: 'End Date', type: 'date' },
      { key: 'PartNum', label: 'Part Number', type: 'text', placeholder: 'e.g. 80025-002' },
    ],
  },
  {
    id: 'labor-costs', name: 'Labor Costs', category: 'Production',
    description: 'Labor hours and costs by resource group and date range',
    params: [
      { key: 'StartDate', label: 'Start Date', type: 'date' },
      { key: 'EndDate', label: 'End Date', type: 'date' },
      { key: 'ResourceGroup', label: 'Resource Group', type: 'text', placeholder: 'e.g. ASSY' },
    ],
  },
  {
    id: 'inventory', name: 'Inventory Report', category: 'Inventory',
    description: 'Full inventory with quantities and product groups',
    params: [
      { key: 'WarehouseCode', label: 'Warehouse', type: 'text', placeholder: 'e.g. MAIN' },
      { key: 'ProdGrpID', label: 'Product Group', type: 'text', placeholder: 'e.g. FRIC' },
    ],
  },
  {
    id: 'inventory-location', name: 'Inventory by Location', category: 'Inventory',
    description: 'Stock quantities by warehouse and bin location',
    params: [
      { key: 'WarehouseCode', label: 'Warehouse', type: 'text', placeholder: 'e.g. MAIN' },
      { key: 'PartNum', label: 'Part Number', type: 'text', placeholder: 'e.g. 80025-002' },
    ],
  },
  {
    id: 'material-location', name: 'Material Location', category: 'Inventory',
    description: 'Track where materials are across all warehouses',
    params: [
      { key: 'PartNum', label: 'Part Number', type: 'text', placeholder: 'e.g. 80025-002' },
    ],
  },
];

export default function EpicorReportsPage() {
  const theme = t();
  const [connectionStatus, setConnectionStatus] = useState<any>(null);
  const [selectedReport, setSelectedReport] = useState(REPORTS[0]);
  const [paramValues, setParamValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');
  const [tableSearch, setTableSearch] = useState('');

  // Login state — removed, using service account via API key
  const isLoggedIn = connectionStatus?.status === 'connected' || connectionStatus?.status === 'service_account';

  // Column visibility (for hiding proprietary columns)
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set());

  // Check connection on mount
  const checkStatus = useCallback(() => {
    fetch('/api/epicor/status/', { credentials: 'same-origin' })
      .then(r => r.json())
      .then(setConnectionStatus)
      .catch(() => setConnectionStatus({ status: 'error', message: 'Failed to reach backend' }));
  }, []);

  useEffect(() => { checkStatus(); }, [checkStatus]);

  const runReport = useCallback(async () => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const params = new URLSearchParams();
      Object.entries(paramValues).forEach(([k, v]) => {
        if (v) params.set(k, v);
      });
      const url = `/api/epicor/reports/${selectedReport.id}/${params.toString() ? '?' + params.toString() : ''}`;
      const res = await fetch(url, { credentials: 'same-origin' });
      if (res.ok) {
        const data = await res.json();
        setResult(data);
        setHiddenColumns(new Set()); // Reset column visibility
      } else {
        const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        setError(err.detail || 'Report failed');
      }
    } catch (e: any) {
      setError(e.message || 'Request failed');
    }
    setLoading(false);
  }, [selectedReport, paramValues]);

  const exportCsv = () => {
    if (!result?.data?.length) return;
    const visibleCols = columns.filter(c => !hiddenColumns.has(c));
    const csv = [
      visibleCols.join(','),
      ...filteredData.map(row => visibleCols.map(h => {
        const val = String(row[h] ?? '').replace(/"/g, '""');
        return val.includes(',') || val.includes('"') || val.includes('\n') ? `"${val}"` : val;
      }).join(','))
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${selectedReport.id}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const filteredData = result?.data?.filter(row => {
    if (!tableSearch) return true;
    const q = tableSearch.toLowerCase();
    return Object.values(row).some(v => String(v ?? '').toLowerCase().includes(q));
  }) || [];

  const columns = result?.data?.length ? Object.keys(result.data[0]) : [];
  const visibleColumns = columns.filter(c => !hiddenColumns.has(c));


  const statusColor = isLoggedIn ? theme.green
    : connectionStatus?.status === 'not_configured' ? theme.yellow
    : theme.red;

  const inputStyle: React.CSSProperties = {
    background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 8,
    padding: '8px 12px', color: theme.text, fontSize: 13, outline: 'none', width: '100%',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%', color: theme.text }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Epicor Reports</h1>
          <p style={{ fontSize: 12, color: theme.textDim, margin: '4px 0 0' }}>
            Read-only access to ERP data via Business Activity Queries
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 11, padding: '4px 10px', borderRadius: 12,
            background: statusColor + '18', color: statusColor, fontWeight: 600,
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusColor }} />
            {isLoggedIn ? `Connected` :
             connectionStatus?.status === 'not_configured' ? 'Awaiting API Key' : 'Not Connected'}
          </span>
        </div>
      </div>

      {/* Not connected banner */}
      {!isLoggedIn && (
        <div style={{
          padding: '20px 24px', borderRadius: 12, background: theme.surface,
          border: `1px solid ${theme.yellow}33`, margin: '20px 0',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: theme.yellow, marginBottom: 6 }}>Awaiting Epicor API Key</div>
          <p style={{ fontSize: 13, color: theme.textDim, margin: 0 }}>
            Once IT provides the API key, this page will connect to Epicor and pull live data.
            In the meantime, check out the Operations Dashboard for a preview of the report layouts.
          </p>
        </div>
      )}

      {/* Main layout: sidebar + content — only when logged in */}
      {isLoggedIn && (
        <div style={{ display: 'flex', gap: 16, flex: 1, minHeight: 0 }}>

          {/* Report Selector Sidebar */}
          <div style={{
            width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 4,
            background: theme.surface, borderRadius: 12, padding: 14,
            border: `1px solid ${theme.border}`, overflow: 'auto',
          }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: theme.textDim, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
              Reports
            </div>
            {['Production', 'Inventory'].map(cat => (
              <div key={cat}>
                <div style={{ fontSize: 9, fontWeight: 700, color: theme.textDim, textTransform: 'uppercase', letterSpacing: 1, padding: '8px 0 4px', marginTop: 4 }}>
                  {cat}
                </div>
                {REPORTS.filter(r => r.category === cat).map(report => (
                  <div key={report.id}
                    onClick={() => { setSelectedReport(report); setParamValues({}); setResult(null); setError(''); }}
                    style={{
                      padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
                      background: selectedReport.id === report.id ? theme.accent + '18' : 'transparent',
                      color: selectedReport.id === report.id ? theme.accent : theme.text,
                      border: selectedReport.id === report.id ? `1px solid ${theme.accent}33` : '1px solid transparent',
                      fontSize: 13, fontWeight: selectedReport.id === report.id ? 600 : 400,
                    }}
                    onMouseEnter={e => { if (selectedReport.id !== report.id) e.currentTarget.style.background = theme.surfaceHover; }}
                    onMouseLeave={e => { if (selectedReport.id !== report.id) e.currentTarget.style.background = 'transparent'; }}>
                    {report.name}
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Report Content */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>

            {/* Parameters */}
            <div style={{ background: theme.surface, borderRadius: 12, padding: 18, border: `1px solid ${theme.border}` }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 4px' }}>{selectedReport.name}</h2>
              <p style={{ fontSize: 12, color: theme.textDim, margin: '0 0 14px' }}>{selectedReport.description}</p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                {selectedReport.params.map(p => (
                  <div key={p.key} style={{ flex: '1 1 180px', maxWidth: 220 }}>
                    <label style={{ fontSize: 10, fontWeight: 600, color: theme.textDim, textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>
                      {p.label}
                    </label>
                    <input type={p.type} placeholder={p.placeholder || ''}
                      value={paramValues[p.key] || ''}
                      onChange={e => setParamValues(prev => ({ ...prev, [p.key]: e.target.value }))}
                      style={inputStyle} />
                  </div>
                ))}
                <button onClick={runReport} disabled={loading}
                  style={{
                    background: theme.accent, color: '#fff', border: 'none', borderRadius: 8,
                    padding: '9px 24px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    opacity: loading ? 0.5 : 1, whiteSpace: 'nowrap',
                  }}>
                  {loading ? 'Loading...' : 'Run Report'}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div style={{ padding: '10px 16px', borderRadius: 8, background: theme.red + '12', border: `1px solid ${theme.red}33`, color: theme.red, fontSize: 13 }}>
                {error}
              </div>
            )}

            {/* Results */}
            {result && (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: theme.surface, borderRadius: 12, border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: `1px solid ${theme.border}` }}>
                  <span style={{ fontSize: 12, color: theme.textDim }}>
                    {result.count} row{result.count !== 1 ? 's' : ''} · {visibleColumns.length}/{columns.length} columns
                    {tableSearch && ` · ${filteredData.length} filtered`}
                  </span>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input placeholder="Filter results..." value={tableSearch}
                      onChange={e => setTableSearch(e.target.value)}
                      style={{ ...inputStyle, width: 180, padding: '5px 10px', fontSize: 12 }} />
                    <button onClick={exportCsv} style={{
                      padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                      background: theme.green + '15', color: theme.green,
                      border: `1px solid ${theme.green}33`, cursor: 'pointer',
                    }}>Export CSV</button>
                  </div>
                </div>

                {/* Column toggle bar */}
                {columns.length > 0 && (
                  <div style={{ padding: '6px 16px', borderBottom: `1px solid ${theme.border}`, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    <span style={{ fontSize: 9, color: theme.textDim, alignSelf: 'center', marginRight: 6 }}>COLUMNS:</span>
                    {columns.map(col => (
                      <button key={col} onClick={() => {
                        const next = new Set(Array.from(hiddenColumns));
                        if (next.has(col)) next.delete(col); else next.add(col);
                        setHiddenColumns(next);
                      }} style={{
                        fontSize: 9, padding: '2px 6px', borderRadius: 4, cursor: 'pointer',
                        background: hiddenColumns.has(col) ? theme.bg : theme.accent + '15',
                        color: hiddenColumns.has(col) ? theme.textDim : theme.accent,
                        border: `1px solid ${hiddenColumns.has(col) ? theme.border : theme.accent + '33'}`,
                        textDecoration: hiddenColumns.has(col) ? 'line-through' : 'none',
                        fontWeight: 500,
                      }}>
                        {col.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>
                )}

                {/* Data table */}
                <div style={{ flex: 1, overflow: 'auto' }}>
                  {visibleColumns.length === 0 ? (
                    <div style={{ padding: 40, textAlign: 'center', color: theme.textDim }}>All columns hidden</div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ borderBottom: `2px solid ${theme.border}` }}>
                          {visibleColumns.map(col => (
                            <th key={col} style={{
                              padding: '8px 12px', textAlign: 'left', fontWeight: 600,
                              color: theme.textDim, fontSize: 10, textTransform: 'uppercase',
                              whiteSpace: 'nowrap', position: 'sticky', top: 0, background: theme.surface,
                            }}>
                              {col.replace(/_/g, ' ')}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredData.map((row: any, i: number) => (
                          <tr key={i} style={{ borderBottom: `1px solid ${theme.border}` }}
                            onMouseEnter={e => e.currentTarget.style.background = theme.surfaceHover}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                            {visibleColumns.map(col => (
                              <td key={col} style={{
                                padding: '7px 12px', color: theme.text,
                                maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              }}>
                                {row[col] != null ? String(row[col]) : '—'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}

            {/* Empty state */}
            {!result && !error && !loading && (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textDim, fontSize: 13 }}>
                Select a report and click "Run Report" to fetch data from Epicor
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
